import useChatInput from "@/composables/v3/useChatInput";
import useTemplates from "@/composables/v3/useTemplates";

// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import i18n from "@/libs/i18n";
import { useWebsockets } from "@/libs/websockets";
import { useChannelsStore } from "@/store/useChannelsStore";
import { getChannelSettingValue, resolveWriteMessageMaxChars } from "@/utils/channels";
import useTypingIndicators from "@/views/modules/messaging-inbox/useTypingIndicators";
import { useToast } from "vue-toastification/composition";
import { getUserData } from "@/auth/getUserData";

// @ts-ignore
export default function useTyping(websockets?: useWebsockets) {
    // @todo toast shouldnt be in a composable.
    // @todo websockets shouldnt be injected this way.
    const toast = useToast();
    const { startTypingIndicator, stopTypingIndicator } = useTypingIndicators(websockets);
    const { message } = useChatInput();

    const onStartTyping = (event: KeyboardEvent): void => {
        const channelsStore = useChannelsStore();
        const templateRegex = /^\/.*/;
        const {
            templateSearchString,
            activeChatTemplatesTab,
            selectedTemplate,
            lastSelectedTemplate,
            resetTemplate,
            isTemplateValid,
            isTemplate,
        } = useTemplates();

        if (!templateRegex.test(message.value)) {
            activeChatTemplatesTab.value = "";
        }

        if ([13, 0, 9, 18, 16].includes(event.keyCode)) {
            return;
        }

        // We keep track of the last selected template for future use
        // For instance checking if we match the selected template while typing
        // @ts-ignore
        if (selectedTemplate.value.id && lastSelectedTemplate.value.id !== selectedTemplate.value.id) {
            lastSelectedTemplate.value = selectedTemplate.value;
        }

        // If we have no template set, but having a last selected template
        // check if the message is a match, if so, "reassign" the selected template again
        // @ts-ignore
        if (lastSelectedTemplate.value.id && "content" in lastSelectedTemplate.value) {
            // Test the last selected template, if we have a match on the message then we re-set the selected template
            const [isValid, validationMessage] = isTemplateValid(
                // @ts-ignore
                lastSelectedTemplate.value.content,
                message.value,
                channelsStore.activeChatChannel
            );
            if (isValid) {
                // @ts-ignore
                selectedTemplate.value = lastSelectedTemplate.value.content;
                isTemplate.value = true;
            } else {
                if (validationMessage) {
                    toast.clear(); // Remove all existing toast before showing a new one.
                    toast(
                        {
                            component: toastificationContent,
                            props: {
                                title: i18n.t("entities.conversation.Conversation"),
                                text: validationMessage,
                                icon: "exclamation-triangle",
                                variant: "warning",
                            },
                        },
                        {
                            timeout: false,
                        }
                    );
                }

                if (Object.keys(selectedTemplate.value).length) {
                    resetTemplate();
                }
            }
        }

        startTypingIndicator();

        if (templateRegex.test(message.value)) {
            templateSearchString.value = message.value.substring(1);

            // @note if you're looking for the template filtering when typing, please check approvedText.vue, approvedMedia.vue and quickReplies.vue
        } else {
            templateSearchString.value = "";
        }

        if (!channelsStore.activeChatChannel) {
            return;
        }

        //----------------------------------------------------------------------

        const maxCharsLimit = resolveWriteMessageMaxChars(
            channelsStore.activeChatChannel.platform,
            channelsStore.activeChatChannel.provider
        );

        if (
            message.value.length >= maxCharsLimit ||
            (getChannelSettingValue(channelsStore.activeChatChannel, "automaticSignature") != "none" &&
                message.value.length >=
                    maxCharsLimit -
                        (getUserData().displayName.length +
                            (getChannelSettingValue(channelsStore.activeChatChannel, "automaticSignature") ==
                            "prepend-agent-name-bold"
                                ? 6
                                : 2)))
        ) {
            toast.clear(); // Remove all existing toast before showing a new one.
            toast(
                {
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Conversation"),
                        text: i18n.t("inbox.reached-character-limit", {
                            charLimit: maxCharsLimit,
                        }),
                        icon: "exclamation-triangle",
                        variant: "warning",
                    },
                },
                {
                    timeout: false,
                }
            );
        }
    };

    const onStopTyping = (): void => stopTypingIndicator();

    return {
        onStartTyping,
        onStopTyping,
    };
}
